const PASTAEL_GIFT_SET_FREE_GIFT_PROPERTY = '_free_gift';
const PASTAEL_GIFT_SET_FREE_GIFT_PROPERTY_VALUE = 'drawstring_bag';
const PASTAEL_GIFT_SET_FREE_GIFT_NOTE_PROPERTY = 'Included drawstring bag';

function pastaelGiftSetFormatMoney(cents) {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(
      cents,
      window.themeVariables?.moneyFormat || window.Shopify?.money_format
    );
  }

  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `£${amount.toFixed(2).replace(/\.00$/, '')}`;
  }
}

if (!customElements.get('pastael-gift-sets')) {
  customElements.define(
    'pastael-gift-sets',
    class PastaelGiftSets extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRailScroll = this.onRailScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.cardsMobileQuery = window.matchMedia('(max-width: 749px)');
        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        this.cardsMobileQuery.addEventListener('change', this.onResize);
        this.initCardsCarousel();
        this.bindGiftSetModals();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        this.cardsMobileQuery?.removeEventListener('change', this.onResize);
        this.cardsFitObserver?.disconnect();
        this.rail?.removeEventListener('scroll', this.onRailScroll);
        if (this.scrollRaf) {
          cancelAnimationFrame(this.scrollRaf);
          this.scrollRaf = 0;
        }
        this.giftSetModals?.forEach((modal) => {
          this.closeGiftSetModal(modal);
          if (modal.dataset.portaled === 'true') modal.remove();
        });
      }

      isMobileCards() {
        return this.cardsMobileQuery.matches;
      }

      initCardsCarousel() {
        this.rail = this.querySelector('[data-gift-sets-rail]');
        this.cardsTrack = this.querySelector('.pastael-gift-sets__cards');
        this.cards = Array.from(this.querySelectorAll('.pastael-gift-sets__card[data-kit-target]'));
        this.dots = Array.from(this.querySelectorAll('[data-cards-dot]'));
        if (!this.rail || this.cards.length === 0) return;

        this.cardsFitObserver = new ResizeObserver(() => {
          this.syncCarouselLayout(true);
        });
        this.cardsFitObserver.observe(this.rail);
        if (this.cardsTrack) {
          this.cardsFitObserver.observe(this.cardsTrack);
        }

        this.rail.addEventListener('scroll', this.onRailScroll, { passive: true });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            const index = Number(dot.dataset.cardsDot);
            const card = this.cards[index];
            if (!card) return;
            this.scrollCardIntoView(card);
            this.activateKit(card.dataset.kitTarget, false);
          });
        });

        this.syncCarouselLayout(false);
        if (this.isMobileCards()) {
          this.syncActiveKitFromCarousel(true);
        } else {
          this.clearKitSelection();
        }
      }

      onResize() {
        this.syncCarouselLayout(true);
        if (this.isMobileCards()) {
          if (!this.classList.contains('is-kit-selected')) {
            this.syncActiveKitFromCarousel(true);
          }
        } else {
          this.clearKitSelection();
        }
      }

      clearKitSelection() {
        this.classList.remove('is-kit-selected');
        this.cards?.forEach((card) => {
          card.classList.remove('is-active');
          card.setAttribute('aria-selected', 'false');
        });
        this.querySelectorAll('.pastael-gift-sets__kit-option[data-kit-target]').forEach((option) => {
          option.classList.remove('is-active');
          option.setAttribute('aria-pressed', 'false');
        });
        this.querySelectorAll('[data-kit-panel]').forEach((panel) => {
          panel.hidden = true;
        });
      }

      cardsFitInRail() {
        if (!this.rail || !this.cardsTrack || this.cards.length === 0) return true;
        return this.rail.scrollWidth <= this.rail.clientWidth + 1;
      }

      syncCarouselLayout(preserveScroll) {
        if (!this.rail || this.cards.length === 0) return;

        const cardsFit =
          this.cardsFitInRail() && (!this.isMobileCards() || this.cards.length <= 1);
        this.classList.toggle('is-cards-fitted', cardsFit);

        if (cardsFit) {
          this.rail.scrollLeft = 0;
          this.cards.forEach((card) => card.classList.remove('is-centered'));
          this.updateActiveCardFromScroll();
          return;
        }

        const previousIndex = this.getActiveCardIndex();

        if (this.isMobileCards()) {
          this.cards.forEach((card, index) => {
            card.classList.toggle('is-centered', index === previousIndex);
          });
          const target = this.cards[previousIndex] || this.cards[0];
          if (!preserveScroll) {
            requestAnimationFrame(() => {
              this.scrollCardToCenter(target, 'auto');
              this.updateActiveCardFromScroll();
            });
          } else {
            this.scrollCardToCenter(target, 'auto');
            this.updateActiveCardFromScroll();
          }
          return;
        }

        this.cards.forEach((card) => card.classList.remove('is-centered'));
        if (!preserveScroll) {
          this.rail.scrollLeft = 0;
        }
        this.updateActiveCardFromScroll();
      }

      onRailScroll() {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = 0;
          this.updateActiveCardFromScroll();
        });
      }

      getCardsPaddingLeft() {
        if (!this.cardsTrack) return 0;
        return Number.parseFloat(getComputedStyle(this.cardsTrack).paddingLeft) || 0;
      }

      scrollCardIntoView(card, behavior = 'smooth') {
        if (this.classList.contains('is-cards-fitted')) return;
        if (this.isMobileCards()) {
          this.scrollCardToCenter(card, behavior);
        } else {
          this.scrollCardToStart(card, behavior);
        }
      }

      scrollCardToCenter(card, behavior = 'smooth') {
        if (!this.rail || !card) return;
        const railRect = this.rail.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const nextLeft =
          this.rail.scrollLeft + (cardRect.left - railRect.left) - (railRect.width - cardRect.width) / 2;
        this.rail.scrollTo({ left: Math.max(0, nextLeft), behavior });
      }

      scrollCardToStart(card, behavior = 'smooth') {
        if (!this.rail || !card) return;
        const targetLeft = Math.max(0, card.offsetLeft - this.getCardsPaddingLeft());
        this.rail.scrollTo({ left: targetLeft, behavior });
      }

      getActiveCardIndex() {
        const activeDot = this.dots.findIndex((dot) => dot.classList.contains('is-active'));
        if (activeDot >= 0) return activeDot;

        const centered = this.cards.findIndex((card) => card.classList.contains('is-centered'));
        if (centered >= 0) return centered;

        return 0;
      }

      updateActiveCardFromScroll() {
        if (!this.rail || this.cards.length === 0) return;

        let activeIndex = 0;

        if (this.isMobileCards()) {
          const railRect = this.rail.getBoundingClientRect();
          const railCenter = railRect.left + railRect.width / 2;
          let closestDistance = Infinity;

          this.cards.forEach((card, index) => {
            const cardRect = card.getBoundingClientRect();
            const cardCenter = cardRect.left + cardRect.width / 2;
            const distance = Math.abs(cardCenter - railCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              activeIndex = index;
            }
          });

          this.cards.forEach((card, index) => {
            card.classList.toggle('is-centered', index === activeIndex);
          });
        } else {
          const scrollLeft = this.rail.scrollLeft + this.getCardsPaddingLeft();
          let closestDistance = Infinity;

          this.cards.forEach((card, index) => {
            const distance = Math.abs(card.offsetLeft - scrollLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              activeIndex = index;
            }
          });
        }

        this.dots.forEach((dot, index) => {
          const active = index === activeIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
        });

        if (this.isMobileCards() || this.classList.contains('is-kit-selected')) {
          this.syncActiveKitFromCarousel(false);
        }
      }

      syncActiveKitFromCarousel(isInitial) {
        if (!this.cards?.length) return;

        const activeIndex = this.getActiveCardIndex();
        const card = this.cards[activeIndex] || this.cards[0];
        const targetId = card?.dataset.kitTarget;
        if (!targetId) return;

        if (
          isInitial &&
          this.isMobileCards() &&
          this.cards.length > 1 &&
          !this.classList.contains('is-cards-fitted')
        ) {
          requestAnimationFrame(() => {
            this.scrollCardToCenter(card, 'auto');
          });
        }

        const currentActive = this.querySelector('.pastael-gift-sets__card.is-active');
        const nextPanel = this.querySelector(`[data-kit-panel="${CSS.escape(targetId)}"]`);
        const panelIsHidden = nextPanel?.hasAttribute('hidden');
        const kitAlreadySynced =
          currentActive?.dataset.kitTarget === targetId && !panelIsHidden;
        if (kitAlreadySynced && !isInitial && this.classList.contains('is-kit-selected')) return;

        this.activateKit(targetId, false);
      }

      onClick(event) {
        const kitTrigger = event.target.closest('[data-kit-target]');
        if (kitTrigger && this.contains(kitTrigger)) {
          event.preventDefault();
          const isCard = kitTrigger.classList.contains('pastael-gift-sets__card');
          if (isCard) {
            this.scrollCardIntoView(kitTrigger);
          }
          this.activateKit(kitTrigger.dataset.kitTarget, isCard);
          return;
        }

        const thumbnail = event.target.closest('[data-gallery-image]');
        if (thumbnail && this.contains(thumbnail)) {
          event.preventDefault();
          this.selectGalleryImage(thumbnail);
          return;
        }

        const previous = event.target.closest('[data-gallery-previous]');
        if (previous && this.contains(previous)) {
          event.preventDefault();
          this.stepGallery(previous.closest('[data-gallery]'), -1);
          return;
        }

        const next = event.target.closest('[data-gallery-next]');
        if (next && this.contains(next)) {
          event.preventDefault();
          this.stepGallery(next.closest('[data-gallery]'), 1);
          return;
        }

        const addButton = event.target.closest('[data-kit-add]');
        if (addButton && this.contains(addButton)) {
          event.preventDefault();
          this.addKit(addButton);
          return;
        }

        const quickAdd = event.target.closest('[data-quick-add]');
        if (quickAdd && this.contains(quickAdd)) {
          event.preventDefault();
          this.addQuickProduct(quickAdd);
        }
      }

      onChange(event) {
        const input = event.target.closest('[data-row-swatches] input[type="radio"]');
        if (!input || !this.contains(input)) return;
        this.updateItemVariant(input.closest('[data-kit-item]'));
      }

      activateKit(targetId, shouldScroll) {
        if (!targetId) return;
        const nextPanel = this.querySelector(`[data-kit-panel="${CSS.escape(targetId)}"]`);
        if (!nextPanel) return;

        const update = () => {
          this.classList.add('is-kit-selected');
          this.querySelectorAll('.pastael-gift-sets__card[data-kit-target]').forEach((card) => {
            const active = card.dataset.kitTarget === targetId;
            card.classList.toggle('is-active', active);
            card.setAttribute('aria-selected', String(active));
          });

          this.querySelectorAll('.pastael-gift-sets__kit-option[data-kit-target]').forEach((option) => {
            const active = option.dataset.kitTarget === targetId;
            option.classList.toggle('is-active', active);
            option.setAttribute('aria-pressed', String(active));
          });

          this.querySelectorAll('[data-kit-panel]').forEach((panel) => {
            panel.hidden = panel !== nextPanel;
          });
        };

        let transition = null;
        if (document.startViewTransition) {
          transition = document.startViewTransition(update);
        } else {
          update();
        }

        if (shouldScroll) {
          const scrollToPanel = () => {
            nextPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
          };
          if (transition?.updateCallbackDone) {
            transition.updateCallbackDone.then(scrollToPanel);
          } else {
            requestAnimationFrame(scrollToPanel);
          }
        }
      }

      selectGalleryImage(thumbnail) {
        const gallery = thumbnail.closest('[data-gallery]');
        const mainImage = gallery?.querySelector('[data-gallery-main]');
        if (!mainImage) return;

        gallery.querySelectorAll('[data-gallery-image]').forEach((button) => {
          button.classList.toggle('is-active', button === thumbnail);
        });

        mainImage.src = thumbnail.dataset.galleryImage;
        mainImage.removeAttribute('srcset');
        mainImage.alt = thumbnail.dataset.galleryAlt || '';
      }

      stepGallery(gallery, direction) {
        const thumbnails = Array.from(gallery?.querySelectorAll('[data-gallery-image]') || []);
        if (thumbnails.length < 2) return;
        const activeIndex = Math.max(
          0,
          thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active'))
        );
        const nextIndex = (activeIndex + direction + thumbnails.length) % thumbnails.length;
        this.selectGalleryImage(thumbnails[nextIndex]);
      }

      parseVariants(row) {
        try {
          return JSON.parse(row.querySelector('[data-kit-variants]')?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      valuesMatch(first, second) {
        return String(first || '').trim().toLowerCase() === String(second || '').trim().toLowerCase();
      }

      updateItemVariant(row) {
        if (!row) return;
        const variants = this.parseVariants(row);
        const colorInput = row.querySelector('[data-row-swatches] input[type="radio"]:checked');
        const colorPosition = Number(row.dataset.colorPosition || 0);
        const sizePosition = Number(row.dataset.sizePosition || 0);
        const colorValue = colorInput?.dataset.optionValue || colorInput?.value || '';
        const sizeValue = row.dataset.sizeValue || '';

        const selectedVariant =
          variants.find((variant) => {
            const colorMatches =
              !colorPosition || this.valuesMatch(variant[`option${colorPosition}`], colorValue);
            const sizeMatches = !sizePosition || !sizeValue || this.valuesMatch(variant[`option${sizePosition}`], sizeValue);
            return colorMatches && sizeMatches;
          }) ||
          variants.find((variant) => !colorPosition || this.valuesMatch(variant[`option${colorPosition}`], colorValue)) ||
          variants[0];

        if (!selectedVariant) return;
        row.dataset.selectedVariantId = String(selectedVariant.id);

        const image = row.querySelector('[data-kit-item-image]');
        if (image && selectedVariant.image) {
          image.src = selectedVariant.image;
          image.removeAttribute('srcset');
        }
      }

      getCartElement() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      getAddUrl() {
        const base = window.routes?.cart_add_url || '/cart/add';
        return base.endsWith('.js') ? base : `${base.replace(/\/$/, '')}.js`;
      }

      async postItems(items) {
        const cart = this.getCartElement();
        const body = { items };

        if (cart?.getSectionsToRender) {
          body.sections = cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',');
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(this.getAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this set to the cart.');
        }

        if (cart?.renderContents && result.sections) {
          cart.renderContents(result);
          cart.classList.remove('is-empty');
        } else if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
        }

        const events = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
        if (events?.cartUpdate && typeof publish === 'function') {
          publish(events.cartUpdate, {
            source: 'pastael-gift-sets',
            productVariantId: items[0]?.id,
            cartData: result,
          });
        }

        return result;
      }

      async addKit(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        const panel = button.closest('[data-kit-panel]');
        const modal =
          panel?.querySelector('[data-gift-set-modal]') ||
          this.giftSetModals?.find((item) => item.dataset.kitPanelId === panel?.dataset.kitPanel);
        if (modal) {
          this.openGiftSetModal(modal, panel);
          return;
        }

        const error = panel?.querySelector('[data-kit-error]');
        const label = button.querySelector('[data-kit-add-label]');
        const originalLabel = label?.textContent || '';
        const quantities = new Map();

        panel?.querySelectorAll('[data-kit-item]').forEach((row) => {
          this.updateItemVariant(row);
          const variantId = Number(row.dataset.selectedVariantId);
          if (!variantId) return;
          quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
        });

        const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
        if (!items.length) {
          if (error) {
            error.textContent = 'Choose a product for every item in this set.';
            error.hidden = false;
          }
          return;
        }

        button.setAttribute('aria-disabled', 'true');
        if (error) error.hidden = true;

        try {
          await this.postItems(items);
          if (label) label.textContent = 'Added';
          setTimeout(() => {
            if (label) label.textContent = originalLabel;
          }, 1800);
        } catch (requestError) {
          if (error) {
            error.textContent = requestError.message;
            error.hidden = false;
          }
        } finally {
          button.removeAttribute('aria-disabled');
        }
      }

      async addQuickProduct(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        const variantId = Number(button.dataset.quickAdd);
        if (!variantId) return;
        const originalLabel = button.textContent;
        button.setAttribute('aria-disabled', 'true');

        try {
          await this.postItems([{ id: variantId, quantity: 1 }]);
          button.textContent = 'Added';
          setTimeout(() => {
            button.textContent = originalLabel;
          }, 1800);
        } catch (error) {
          console.error('[pastael-gift-sets]', error);
        } finally {
          button.removeAttribute('aria-disabled');
        }
      }

      bindGiftSetModals() {
        this.giftSetModals = Array.from(this.querySelectorAll('[data-gift-set-modal]'));
        this.giftSetModals.forEach((modal) => {
          if (modal.dataset.bound === 'true') return;
          modal.dataset.bound = 'true';

          const dialog = modal.querySelector('[data-gift-set-dialog]');
          modal.querySelectorAll('[data-gift-set-modal-close]').forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              this.closeGiftSetModal(modal);
            });
          });
          dialog?.addEventListener('click', (event) => {
            if (event.target === dialog) this.closeGiftSetModal(modal);
          });
          dialog?.addEventListener('close', () => {
            document.body.classList.remove('overflow-hidden');
          });
          modal.querySelector('[data-gift-set-picker]')?.addEventListener('change', (event) => {
            if (event.target?.type !== 'radio' || !event.target.closest('[data-gift-swatches]')) return;
            this.syncGiftSetModalVariant(modal);
          });
          modal.querySelector('[data-gift-set-modal-add]')?.addEventListener('click', (event) => {
            event.preventDefault();
            this.submitGiftSetModal(modal);
          });

          this.bindGiftSetEmbroidery(modal);
        });
      }

      getGiftSetPanel(modal) {
        const panelId = modal?.dataset.kitPanelId;
        if (!panelId) return null;
        return this.querySelector(`[data-kit-panel="${CSS.escape(panelId)}"]`);
      }

      parseGiftSetVariants(modal) {
        try {
          return JSON.parse(modal.querySelector('[data-gift-variants]')?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      optionValuesMatch(left, right) {
        return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
      }

      getSelectedGiftSetColorInput(modal) {
        return modal.querySelector('[data-gift-swatches] input[type="radio"]:checked');
      }

      resolveGiftSetVariant(modal) {
        const variants = this.parseGiftSetVariants(modal);
        const colorInput = this.getSelectedGiftSetColorInput(modal);
        const colorValue = colorInput?.dataset.optionValue || colorInput?.value || '';
        const colorPosition = Number(colorInput?.dataset.optionPosition || 0);

        return (
          variants.find((variant) => {
            if (!colorPosition) return true;
            const optionValue =
              colorPosition === 1 ? variant.option1 : colorPosition === 2 ? variant.option2 : variant.option3;
            return this.optionValuesMatch(optionValue, colorValue);
          }) ||
          variants.find((variant) => variant.available) ||
          variants[0] ||
          null
        );
      }

      giftSetImageUrl(url, width) {
        if (!url) return '';
        if (/[?&]width=\d+/.test(url)) return url.replace(/width=\d+/, `width=${width}`);
        return url;
      }

      selectDefaultGiftSetSwatch(modal) {
        if (this.getSelectedGiftSetColorInput(modal)) return;
        const first = modal.querySelector('[data-gift-swatches] input[type="radio"]:not([hidden]):not([disabled])');
        if (first) first.checked = true;
      }

      syncGiftSetModalVariant(modal) {
        const variant = this.resolveGiftSetVariant(modal);
        const hidden = modal.querySelector('[data-gift-variant-id]');
        const colorInput = this.getSelectedGiftSetColorInput(modal);
        const colorName = modal.querySelector('[data-gift-set-color-name]');
        const imageEl = modal.querySelector('[data-gift-set-image]');

        if (hidden) hidden.value = variant?.id ? String(variant.id) : '';
        if (colorName) colorName.textContent = colorInput?.dataset.optionValue || colorInput?.value || '';

        const imageUrl = variant?.image_large || variant?.image;
        if (imageEl && imageUrl) {
          const large = this.giftSetImageUrl(imageUrl, 1200);
          const medium = this.giftSetImageUrl(imageUrl, 720);
          imageEl.src = large;
          imageEl.srcset = `${medium} 720w, ${large} 1200w`;
          imageEl.alt = colorInput?.dataset.optionValue || colorInput?.value || imageEl.alt;
        }

        this.clearGiftSetModalError(modal);
        return variant;
      }

      bindGiftSetEmbroidery(modal) {
        const root = modal.querySelector('[data-pastael-embroidery]');
        if (!root) return;

        const toggle = root.querySelector('[data-embroidery-toggle]');
        const initials = root.querySelector('[data-embroidery-initials]');
        const productJson = root.querySelector('[data-embroidery-product-json]');

        try {
          root._embroideryProductData = JSON.parse(productJson?.textContent || '{}');
        } catch (error) {
          root._embroideryProductData = {};
        }

        root.querySelectorAll('.pastael-pdp__embroidery-fieldset').forEach((fieldset) => {
          const options = fieldset.querySelectorAll('[data-embroidery-option]');
          if (options.length > 0 && !fieldset.querySelector('[data-embroidery-option]:checked')) {
            options[0].checked = true;
          }
        });

        root.addEventListener('change', (event) => {
          if (event.target === toggle || event.target.matches('[data-embroidery-option]')) {
            this.syncGiftSetEmbroidery(modal);
          }
        });

        initials?.addEventListener('input', (event) => {
          const sanitized = String(event.target.value || '')
            .replace(/[^a-z]/gi, '')
            .slice(0, 3)
            .toUpperCase();
          if (event.target.value !== sanitized) event.target.value = sanitized;
          const count = root.querySelector('[data-embroidery-count]');
          if (count) count.textContent = `${sanitized.length}/3`;
          this.clearGiftSetEmbroideryError(modal);
          this.updateGiftSetEmbroideryPreview(modal);
        });

        this.syncGiftSetEmbroidery(modal);
      }

      getGiftSetEmbroiderySelectedOptions(root) {
        const selected = [];
        root?.querySelectorAll('[data-embroidery-option]:checked').forEach((input) => {
          const position = Number(input.dataset.embroideryOptionPosition);
          if (position > 0) selected[position - 1] = input.value;
        });
        return selected;
      }

      getGiftSetEmbroideryPreviewColor(input) {
        const configured = String(input?.dataset.embroideryColor || '').trim();
        if (configured) return configured;

        const color = String(input?.value || '').trim().toLowerCase();
        const fallback = {
          white: '#ffffff',
          yellow: '#f6d884',
          red: '#8f1b0d',
          black: '#000000',
          brown: '#574138',
          'baby blue': '#A2CFFE',
          navy: '#15144f',
          'navy blue': '#15144f',
          blue: '#15144f',
        };
        return fallback[color] || '#ffffff';
      }

      syncGiftSetEmbroidery(modal) {
        const root = modal.querySelector('[data-pastael-embroidery]');
        if (!root) {
          this.updateGiftSetModalTotal(modal, null);
          return null;
        }

        const toggle = root.querySelector('[data-embroidery-toggle]');
        const panel = root.querySelector('[data-embroidery-panel]');
        const expanded = Boolean(toggle?.checked);
        const selectedOptions = this.getGiftSetEmbroiderySelectedOptions(root);
        const variants = Array.isArray(root._embroideryProductData?.variants)
          ? root._embroideryProductData.variants
          : [];

        const variant =
          variants.find((item) =>
            (item.options || []).every(
              (option, index) =>
                selectedOptions[index] == null || this.optionValuesMatch(option, selectedOptions[index])
            )
          ) || null;

        const variantInput = root.querySelector('[data-embroidery-variant-id]');
        if (variantInput) variantInput.value = variant?.id || '';

        if (panel) panel.hidden = !expanded;
        toggle?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        root.classList.toggle('is-active', expanded);
        root.querySelector('[data-embroidery-collapsed-price]')?.toggleAttribute('hidden', expanded);
        root.querySelector('[data-embroidery-selected-price]')?.toggleAttribute('hidden', !expanded);

        const selectedPrice = root.querySelector('[data-embroidery-selected-price]');
        if (selectedPrice && variant?.price != null) {
          selectedPrice.textContent = pastaelGiftSetFormatMoney(variant.price);
        }

        const colorInput = root.querySelector('[data-embroidery-option][data-embroidery-color]:checked');
        const colorName = root.querySelector('[data-embroidery-color-name]');
        if (colorInput && colorName) colorName.textContent = colorInput.value;

        if (!expanded) this.clearGiftSetEmbroideryError(modal);
        this.updateGiftSetEmbroideryPreview(modal);
        this.updateGiftSetModalTotal(modal, expanded ? variant : null);
        return variant;
      }

      updateGiftSetEmbroideryPreview(modal) {
        const preview = modal.querySelector('[data-embroidery-preview]');
        const root = modal.querySelector('[data-pastael-embroidery]');
        if (!preview) return;

        const toggle = root?.querySelector('[data-embroidery-toggle]');
        const initialsInput = root?.querySelector('[data-embroidery-initials]');
        const initials = String(initialsInput?.value || '').trim().toUpperCase();
        const enabled = Boolean(toggle?.checked);
        const fontInput = root?.querySelector('[data-embroidery-font]:checked');
        const colorInput = root?.querySelector('[data-embroidery-option][data-embroidery-color]:checked');

        preview.className = preview.className
          .split(' ')
          .filter((className) => !className.startsWith('pastael-pdp__embroidery-preview--'))
          .join(' ');
        const fontHandle = fontInput?.dataset.embroideryFont || 'bodoni';
        preview.classList.add(`pastael-pdp__embroidery-preview--${fontHandle}`);
        const fontInputs = root?.querySelectorAll('[data-embroidery-font]');
        const fontIndex =
          fontInput && fontInputs?.length ? Array.from(fontInputs).indexOf(fontInput) : -1;
        preview.classList.toggle('pastael-pdp__embroidery-preview--font-third', fontIndex === 2);
        preview.style.setProperty('--embroidery-preview-color', this.getGiftSetEmbroideryPreviewColor(colorInput));
        preview.textContent = initials;
        preview.hidden = !enabled || initials.length === 0;
      }

      updateGiftSetModalTotal(modal, embroideryVariant) {
        const totalEl = modal.querySelector('[data-gift-set-modal-total]');
        if (!totalEl) return;
        const basePrice = Number(modal.dataset.kitBasePrice || 0);
        const embroideryPrice = embroideryVariant?.price != null ? Number(embroideryVariant.price) : 0;
        totalEl.textContent = pastaelGiftSetFormatMoney(basePrice + embroideryPrice);
      }

      clearGiftSetModalError(modal) {
        const error = modal.querySelector('[data-gift-set-modal-error]');
        if (!error) return;
        error.hidden = true;
        error.textContent = '';
      }

      showGiftSetModalError(modal, message) {
        const error = modal.querySelector('[data-gift-set-modal-error]');
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
      }

      clearGiftSetEmbroideryError(modal) {
        const error = modal.querySelector('[data-embroidery-error]');
        const initials = modal.querySelector('[data-embroidery-initials]');
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
        initials?.removeAttribute('aria-invalid');
      }

      showGiftSetEmbroideryError(modal, message) {
        const root = modal.querySelector('[data-pastael-embroidery]');
        const error = root?.querySelector('[data-embroidery-error]');
        const initials = root?.querySelector('[data-embroidery-initials]');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        initials?.setAttribute('aria-invalid', 'true');
        initials?.focus();
      }

      getGiftSetEmbroiderySelection(modal) {
        const root = modal.querySelector('[data-pastael-embroidery]');
        const toggle = root?.querySelector('[data-embroidery-toggle]');
        if (!root || !toggle?.checked) return null;

        const initials = String(root.querySelector('[data-embroidery-initials]')?.value || '')
          .replace(/[^a-z]/gi, '')
          .slice(0, 3)
          .toUpperCase();
        if (initials.length < 1) {
          return {
            error: root.dataset.initialsRequired || 'Enter 1 to 3 letters for your embroidery.',
          };
        }

        const variant = this.syncGiftSetEmbroidery(modal);
        if (!variant?.id || variant.available === false) {
          return {
            error: root.dataset.combinationUnavailable || 'This embroidery combination is unavailable.',
          };
        }

        const fontInput = root.querySelector('[data-embroidery-font]:checked');
        const colorInput = root.querySelector(
          '[data-embroidery-option][data-embroidery-color]:checked'
        );
        const font =
          fontInput?.value ||
          fontInput?.closest('label')?.querySelector('.pastael-pdp__embroidery-font-display')?.textContent ||
          '';
        const color = colorInput?.value || '';

        return {
          variantId: Number(variant.id),
          price: Number(variant.price) || 0,
          initials,
          font: String(font).trim(),
          color: String(color).trim(),
        };
      }

      getGiftSetEmbroideryWarehouseProperties(selection) {
        if (!selection?.initials) return {};

        const properties = {
          Letters: selection.initials,
          Initials: selection.initials,
        };
        if (selection.font) properties.Font = selection.font;
        if (selection.color) properties.Color = selection.color;
        return properties;
      }

      createEmbroideryGroupId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `embroidery-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      openGiftSetModal(modal, panel) {
        const dialog = modal.querySelector('[data-gift-set-dialog]');
        if (!dialog) return;

        if (modal.dataset.portaled !== 'true') {
          document.body.appendChild(modal);
          modal.dataset.portaled = 'true';
        }

        this.selectDefaultGiftSetSwatch(modal);
        this.syncGiftSetModalVariant(modal);
        this.syncGiftSetEmbroidery(modal);
        this.clearGiftSetModalError(modal);

        try {
          dialog.showModal();
        } catch (error) {
          console.error('[pastael-gift-sets] showModal failed', error);
          return;
        }

        document.body.classList.add('overflow-hidden');
        modal.querySelector('[data-gift-set-modal-close]')?.focus();
        this.activeGiftSetPanel = panel || this.getGiftSetPanel(modal);
      }

      closeGiftSetModal(modal) {
        const dialog = modal?.querySelector('[data-gift-set-dialog]');
        if (dialog?.open) dialog.close();
      }

      collectKitItems(panel, { skipGift = false } = {}) {
        const quantities = new Map();
        panel?.querySelectorAll('[data-kit-item]').forEach((row) => {
          if (skipGift && row.hasAttribute('data-kit-gift')) return;
          this.updateItemVariant(row);
          const variantId = Number(row.dataset.selectedVariantId);
          if (!variantId) return;
          quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
        });
        return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
      }

      async submitGiftSetModal(modal) {
        const button = modal.querySelector('[data-gift-set-modal-add]');
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const panel = this.activeGiftSetPanel || this.getGiftSetPanel(modal);
        const hasSwatches = Boolean(modal.querySelector('[data-gift-swatches]'));
        const variant = this.syncGiftSetModalVariant(modal);

        if (hasSwatches && !this.getSelectedGiftSetColorInput(modal)) {
          this.showGiftSetModalError(
            modal,
            modal.dataset.colorRequired || 'Choose a bag colour for your free gift before adding to cart.'
          );
          return;
        }

        if (!variant?.id) {
          this.showGiftSetModalError(
            modal,
            modal.dataset.colorRequired || 'Choose a bag colour for your free gift before adding to cart.'
          );
          return;
        }

        const embroiderySelection = this.getGiftSetEmbroiderySelection(modal);
        if (embroiderySelection?.error) {
          this.showGiftSetEmbroideryError(modal, embroiderySelection.error);
          return;
        }

        const items = this.collectKitItems(panel, { skipGift: true });
        if (!items.length) {
          this.showGiftSetModalError(modal, 'Choose a product for every item in this set.');
          return;
        }

        const colorValue = (
          this.getSelectedGiftSetColorInput(modal)?.dataset.optionValue ||
          this.getSelectedGiftSetColorInput(modal)?.value ||
          ''
        ).trim();
        const giftProperties = {
          [PASTAEL_GIFT_SET_FREE_GIFT_PROPERTY]: PASTAEL_GIFT_SET_FREE_GIFT_PROPERTY_VALUE,
        };
        if (colorValue) giftProperties[PASTAEL_GIFT_SET_FREE_GIFT_NOTE_PROPERTY] = colorValue;

        let groupId = null;
        if (embroiderySelection?.variantId) {
          groupId = this.createEmbroideryGroupId();
          giftProperties._pastael_embroidery_group = groupId;
          Object.assign(giftProperties, this.getGiftSetEmbroideryWarehouseProperties(embroiderySelection));
        }

        items.push({
          id: Number(variant.id),
          quantity: 1,
          properties: giftProperties,
        });

        if (embroiderySelection?.variantId) {
          items.push({
            id: embroiderySelection.variantId,
            quantity: 1,
            properties: {
              ...this.getGiftSetEmbroideryWarehouseProperties(embroiderySelection),
              _pastael_embroidery: 'true',
              _pastael_embroidery_group: groupId,
              _pastael_parent_variant: String(variant.id),
            },
          });
        }

        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        this.clearGiftSetModalError(modal);

        try {
          await this.postItems(items);
          this.closeGiftSetModal(modal);
        } catch (requestError) {
          this.showGiftSetModalError(modal, requestError.message);
        } finally {
          button.removeAttribute('aria-disabled');
          button.disabled = false;
        }
      }
    }
  );
}
